import { api } from './apiClient';

/**
 * Typed client for every /api/v1/hr endpoint the HR module UI consumes.
 *
 * Every apiClient verb auto-unwraps `{status,data}` to `data` (see
 * apiClient.ts). Most endpoints still nest the payload one level deeper
 * under a named key (e.g. `{department: {...}}`), so mutations below that
 * need the bare record reach through that key via `unwrap()`.
 *
 * Numeric DECIMAL columns (ctc_annual, gross_total, days, entitled, ...) come
 * back from mysql2 as strings by default (no `decimalNumbers: true` on the
 * pool) — typed here as `number | string` so callers must parse before doing
 * arithmetic or formatting.
 */

// ---------------------------------------------------------------------------
// Departments / designations
// ---------------------------------------------------------------------------

export interface Department {
  id: number;
  tenant_id: number;
  dept_code: string;
  dept_name: string;
  parent_id: number | null;
  head_employee_id: number | null;
  cost_center_id: number | null;
  is_active: boolean | number;
  created_at: string;
  head_employee_name?: string | null;
}

export interface Designation {
  id: number;
  tenant_id: number;
  title: string;
  grade: string | null;
  is_active: boolean | number;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Employees
// ---------------------------------------------------------------------------

export type EmployeeStatus = 'ACTIVE' | 'ON_LEAVE' | 'NOTICE' | 'EXITED' | 'SUSPENDED';
export type EmploymentType = 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'INTERN';

export interface EmployeeListItem {
  id: number;
  employee_code: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  date_of_joining: string;
  date_of_leaving: string | null;
  department_id: number | null;
  designation_id: number | null;
  manager_id: number | null;
  employment_type: EmploymentType;
  status: EmployeeStatus;
  work_location: string | null;
  ctc_annual: number | string | null;
  created_at: string;
  dept_name: string | null;
  designation_title: string | null;
  manager_name: string | null;
}

/** Full row — includes the fields the list query deliberately omits. */
export interface Employee extends EmployeeListItem {
  tenant_id: number;
  user_id: number | null;
  pan: string | null;
  aadhaar_last4: string | null;
  bank_account: string | null;
  bank_ifsc: string | null;
  pf_number: string | null;
  uan: string | null;
  esi_number: string | null;
  photo_url: string | null;
  created_by: number | null;
  updated_at: string;
}

export interface EmployeePagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EmployeePayload {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  dateOfJoining: string;
  departmentId?: number;
  designationId?: number;
  managerId?: number;
  employmentType?: EmploymentType;
  workLocation?: string;
  ctcAnnual?: number;
  pan?: string;
  aadhaarLast4?: string;
  bankAccount?: string;
  bankIfsc?: string;
  pfNumber?: string;
  uan?: string;
  esiNumber?: string;
  photoUrl?: string;
}

// ---------------------------------------------------------------------------
// Attendance
// ---------------------------------------------------------------------------

export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'HALF_DAY' | 'ON_LEAVE' | 'HOLIDAY' | 'WEEK_OFF' | 'WFH';

export interface AttendanceRecord {
  id: number;
  tenant_id: number;
  employee_id: number;
  attendance_date: string;
  check_in: string | null;
  check_out: string | null;
  status: AttendanceStatus;
  worked_hours: number | string | null;
  overtime_hours: number | string;
  source: 'MANUAL' | 'BIOMETRIC' | 'WEB' | 'MOBILE';
  remarks: string | null;
  created_at: string;
  employee_name: string;
  employee_code: string;
}

export interface TodayAttendanceSummary {
  date: string;
  totalActiveEmployees: number;
  marked: number;
  notMarked: number;
  byStatus: Record<AttendanceStatus, number>;
}

// ---------------------------------------------------------------------------
// Leave
// ---------------------------------------------------------------------------

export interface LeaveType {
  id: number;
  tenant_id: number;
  code: string;
  name: string;
  annual_quota: number | string;
  is_paid: boolean | number;
  carry_forward: boolean | number;
  is_active: boolean | number;
}

export type LeaveRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface LeaveRequest {
  id: number;
  tenant_id: number;
  employee_id: number;
  leave_type_id: number;
  from_date: string;
  to_date: string;
  days: number | string;
  reason: string | null;
  status: LeaveRequestStatus;
  approved_by: number | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
  employee_name: string;
  employee_code: string;
  leave_type_name: string;
  leave_type_code: string;
}

export interface LeaveBalance {
  id: number;
  tenant_id: number;
  employee_id: number;
  leave_type_id: number;
  financial_year: string;
  entitled: number | string;
  taken: number | string;
  pending: number | string;
  leave_type_name: string;
  leave_type_code: string;
}

// ---------------------------------------------------------------------------
// Holidays
// ---------------------------------------------------------------------------

export interface Holiday {
  id: number;
  tenant_id: number;
  holiday_date: string;
  name: string;
  is_optional: boolean | number;
}

// ---------------------------------------------------------------------------
// Payroll
// ---------------------------------------------------------------------------

export type PayrollRunStatus = 'DRAFT' | 'PROCESSING' | 'PENDING_APPROVAL' | 'APPROVED' | 'PAID' | 'CANCELLED';

export interface PayrollRun {
  id: number;
  tenant_id: number;
  run_code: string;
  period_month: number;
  period_year: number;
  status: PayrollRunStatus;
  employee_count: number;
  gross_total: number | string;
  deduction_total: number | string;
  net_total: number | string;
  processed_by: number | null;
  processed_at: string | null;
  created_at: string;
}

export interface Payslip {
  id: number;
  tenant_id: number;
  payroll_run_id: number;
  employee_id: number;
  basic: number | string;
  hra: number | string;
  allowances: number | string;
  overtime_pay: number | string;
  gross: number | string;
  pf_employee: number | string;
  esi_employee: number | string;
  tds: number | string;
  other_deductions: number | string;
  net_pay: number | string;
  days_paid: number | string;
  lop_days: number | string;
  employee_name: string;
  employee_code: string;
}

export interface PayrollRunDetail extends PayrollRun {
  payslips: Payslip[];
}

export interface ProcessPayrollResult {
  processed: boolean;
  employeeCount: number;
  grossTotal: number;
  deductionTotal: number;
  netTotal: number;
}

// ---------------------------------------------------------------------------
// Recruitment
// ---------------------------------------------------------------------------

export type JobOpeningStatus = 'OPEN' | 'ON_HOLD' | 'CLOSED' | 'FILLED';
export type JobApplicationStage = 'APPLIED' | 'SCREENING' | 'INTERVIEW' | 'OFFER' | 'HIRED' | 'REJECTED';

export interface JobOpening {
  id: number;
  tenant_id: number;
  job_code: string;
  title: string;
  department_id: number | null;
  openings: number;
  status: JobOpeningStatus;
  target_date: string | null;
  created_at: string;
  dept_name: string | null;
}

export interface JobApplication {
  id: number;
  tenant_id: number;
  job_opening_id: number;
  candidate_name: string;
  email: string | null;
  phone: string | null;
  stage: JobApplicationStage;
  resume_url: string | null;
  rating: number | null;
  created_at: string;
  updated_at: string;
  job_title: string;
  job_code: string;
}

// ---------------------------------------------------------------------------
// Dashboard summary
// ---------------------------------------------------------------------------

export interface HrSummary {
  headcount: number;
  presentToday: number;
  absentToday: number;
  onLeaveToday: number;
  pendingLeaveRequests: number;
  openPositions: number;
  birthdaysThisWeek: number;
  joiningThisMonth: number;
  /** Null when not computable — never render as 0. */
  attritionRate: number | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Pulls the named key out of a PUT/PATCH/DELETE response body.
 * apiClient already unwraps the outer `{status, data}` envelope for every
 * verb (see apiClient.ts), so `body` here is already `{[key]: T}` — this
 * just reaches through the inner named key most endpoints nest under.
 */
const unwrap = <T,>(body: any, key: string): T => body?.[key];

type QueryParams = Record<string, string | number | boolean | undefined | null>;

const qs = (params?: QueryParams): string => {
  if (!params) return '';
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

export const hrService = {
  // ── Departments ──────────────────────────────────────────────────────────
  getDepartments: (includeInactive?: boolean): Promise<{ departments: Department[]; count: number }> =>
    api.get(`/hr/departments${qs({ includeInactive })}`),

  createDepartment: (payload: {
    deptCode: string;
    deptName: string;
    parentId?: number;
    headEmployeeId?: number;
    costCenterId?: number;
  }): Promise<{ department: Department }> => api.post('/hr/departments', payload),

  updateDepartment: (
    id: number,
    payload: Partial<{
      deptCode: string;
      deptName: string;
      parentId: number | null;
      headEmployeeId: number | null;
      costCenterId: number | null;
      isActive: boolean;
    }>
  ): Promise<Department> => api.put(`/hr/departments/${id}`, payload).then((r) => unwrap<Department>(r, 'department')),

  // ── Designations ─────────────────────────────────────────────────────────
  getDesignations: (includeInactive?: boolean): Promise<{ designations: Designation[]; count: number }> =>
    api.get(`/hr/designations${qs({ includeInactive })}`),

  createDesignation: (payload: { title: string; grade?: string }): Promise<{ designation: Designation }> =>
    api.post('/hr/designations', payload),

  // ── Employees ────────────────────────────────────────────────────────────
  getEmployees: (filters?: {
    status?: string;
    departmentId?: number;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ employees: EmployeeListItem[]; pagination: EmployeePagination }> =>
    api.get(`/hr/employees${qs(filters)}`),

  getEmployeeById: (id: number): Promise<{ employee: Employee }> => api.get(`/hr/employees/${id}`),

  createEmployee: (payload: EmployeePayload): Promise<{ employee: Employee }> => api.post('/hr/employees', payload),

  updateEmployee: (id: number, payload: EmployeePayload): Promise<Employee> =>
    api.put(`/hr/employees/${id}`, payload).then((r) => unwrap<Employee>(r, 'employee')),

  updateEmployeeStatus: (id: number, status: EmployeeStatus, dateOfLeaving?: string): Promise<Employee> =>
    api.patch(`/hr/employees/${id}/status`, { status, dateOfLeaving }).then((r) => unwrap<Employee>(r, 'employee')),

  // ── Attendance ───────────────────────────────────────────────────────────
  getAttendance: (filters?: {
    date?: string;
    employeeId?: number;
    from?: string;
    to?: string;
  }): Promise<{ attendance: AttendanceRecord[]; count: number }> => api.get(`/hr/attendance${qs(filters)}`),

  getTodaySummary: (): Promise<TodayAttendanceSummary> => api.get('/hr/attendance/today-summary'),

  checkIn: (employeeId: number): Promise<{ attendance: AttendanceRecord }> =>
    api.post('/hr/attendance/check-in', { employeeId }),

  checkOut: (employeeId: number): Promise<{ attendance: AttendanceRecord }> =>
    api.post('/hr/attendance/check-out', { employeeId }),

  markAttendance: (
    records: Array<{
      employeeId: number;
      date: string;
      checkIn?: string;
      checkOut?: string;
      status?: AttendanceStatus;
      workedHours?: number;
      overtimeHours?: number;
      remarks?: string;
    }>
  ): Promise<{ records: Array<{ employeeId: number; date: string; status: string }> }> =>
    api.post('/hr/attendance/mark', { records }),

  // ── Leave ────────────────────────────────────────────────────────────────
  getLeaveTypes: (includeInactive?: boolean): Promise<{ leaveTypes: LeaveType[]; count: number }> =>
    api.get(`/hr/leave-types${qs({ includeInactive })}`),

  createLeaveType: (payload: {
    code: string;
    name: string;
    annualQuota?: number;
    isPaid?: boolean;
    carryForward?: boolean;
  }): Promise<{ leaveType: LeaveType }> => api.post('/hr/leave-types', payload),

  getLeaveRequests: (filters?: {
    status?: string;
    employeeId?: number;
  }): Promise<{ leaveRequests: LeaveRequest[]; count: number }> => api.get(`/hr/leave-requests${qs(filters)}`),

  createLeaveRequest: (payload: {
    employeeId: number;
    leaveTypeId: number;
    fromDate: string;
    toDate: string;
    reason?: string;
  }): Promise<{ leaveRequest: LeaveRequest }> => api.post('/hr/leave-requests', payload),

  updateLeaveRequestStatus: (id: number, status: 'APPROVED' | 'REJECTED' | 'CANCELLED'): Promise<LeaveRequest> =>
    api.patch(`/hr/leave-requests/${id}/status`, { status }).then((r) => unwrap<LeaveRequest>(r, 'leaveRequest')),

  getLeaveBalances: (employeeId: number): Promise<{ balances: LeaveBalance[] }> =>
    api.get(`/hr/leave-balances/${employeeId}`),

  // ── Holidays ─────────────────────────────────────────────────────────────
  getHolidays: (filters?: { year?: number }): Promise<{ holidays: Holiday[]; count: number }> =>
    api.get(`/hr/holidays${qs(filters)}`),

  createHoliday: (payload: { holidayDate: string; name: string; isOptional?: boolean }): Promise<{ holiday: Holiday }> =>
    api.post('/hr/holidays', payload),

  // ── Payroll ──────────────────────────────────────────────────────────────
  getPayrollRuns: (): Promise<{ payrollRuns: PayrollRun[]; count: number }> => api.get('/hr/payroll-runs'),

  createPayrollRun: (payload: { periodMonth: number; periodYear: number }): Promise<{ payrollRun: PayrollRunDetail }> =>
    api.post('/hr/payroll-runs', payload),

  getPayrollRunById: (id: number): Promise<{ payrollRun: PayrollRunDetail }> => api.get(`/hr/payroll-runs/${id}`),

  processPayrollRun: (id: number): Promise<ProcessPayrollResult> => api.post(`/hr/payroll-runs/${id}/process`, {}),

  updatePayrollRunStatus: (id: number, status: PayrollRunStatus): Promise<PayrollRunDetail> =>
    api.patch(`/hr/payroll-runs/${id}/status`, { status }).then((r) => unwrap<PayrollRunDetail>(r, 'payrollRun')),

  // ── Recruitment ──────────────────────────────────────────────────────────
  getJobOpenings: (filters?: { status?: string }): Promise<{ jobOpenings: JobOpening[]; count: number }> =>
    api.get(`/hr/job-openings${qs(filters)}`),

  createJobOpening: (payload: {
    title: string;
    departmentId?: number;
    openings?: number;
    targetDate?: string;
  }): Promise<{ jobOpening: JobOpening }> => api.post('/hr/job-openings', payload),

  getJobApplications: (filters?: {
    openingId?: number;
    stage?: string;
  }): Promise<{ jobApplications: JobApplication[]; count: number }> => api.get(`/hr/job-applications${qs(filters)}`),

  createJobApplication: (payload: {
    jobOpeningId: number;
    candidateName: string;
    email?: string;
    phone?: string;
    resumeUrl?: string;
  }): Promise<{ jobApplication: JobApplication }> => api.post('/hr/job-applications', payload),

  updateJobApplicationStage: (id: number, stage: JobApplicationStage): Promise<JobApplication> =>
    api.patch(`/hr/job-applications/${id}/stage`, { stage }).then((r) => unwrap<JobApplication>(r, 'jobApplication')),

  // ── Dashboard ────────────────────────────────────────────────────────────
  getSummary: (): Promise<HrSummary> => api.get('/hr/summary'),
};

export default hrService;
